'use client';

import LoadingBar from '@components/@ui/LoadingBar/LoadingBar';
import {FC, useEffect, useRef} from 'react';
import styles from './PageLoadingBar.module.css';

/*
SSR navigation start - we send a CustomEvent from the nextjs router reducer patch on navigate events. See patches/next+x.x.x.patch.
SSR navigation end - when SSR navigation ends, there are some elements that always get replaced in the document. One such element is <meta charset="utf-8">. We can use this mutation to detect the end of the SSR navigation event.
*/

const TRIGGER_EL_SELECTOR = 'meta[charset="utf-8"]';
const NAV_START_EVENT = 'nextjs-router-navigation-start';
const NAV_NO_OP_EVENT = 'nextjs-router-navigation-no-op';

const PageLoadingBar: FC = () => {
    const loadingBar = useRef<LoadingBar>(null);
    const prevTriggerEl = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const handleNavigationStart = () => {
            loadingBar.current?.setProgress(0.6);
        };

        const handleNavigationEnd = () => {
            loadingBar.current?.setProgress(1);
        };

        const handleNavigationNoOp = () => {
            loadingBar.current?.reset();
        };

        // watc
        const observer = new MutationObserver(mutations => {
            const newTriggerEl = document.querySelector(TRIGGER_EL_SELECTOR);
            if (newTriggerEl === prevTriggerEl.current) {
                return;
            }

            const isInitialPageLoad = !prevTriggerEl.current;
            prevTriggerEl.current = newTriggerEl as HTMLElement;

            if (isInitialPageLoad) {
                return;
            }

            handleNavigationEnd();
        });

        const el = document.querySelector('html');

        if (!el) {
            return;
        }

        observer.observe(el, {
            childList: true,
            subtree: true,
        });

        // watch CustomEvent from a nextjs router reducer patch
        window.addEventListener(NAV_START_EVENT, handleNavigationStart);
        window.addEventListener(NAV_NO_OP_EVENT, handleNavigationNoOp);

        return () => {
            observer.disconnect();
            window.removeEventListener(NAV_START_EVENT, handleNavigationStart);
            window.removeEventListener(NAV_NO_OP_EVENT, handleNavigationNoOp);
        };
    }, []);

    return (
        <LoadingBar
            ref={loadingBar}
            className={styles.container}
            id="page-loading-bar"
        />
    );
};

export default PageLoadingBar;
